<template>
  <BaseDialog max-width="800" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="modalTitle" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <div class="stepper_container">
            <v-stepper
              v-if="needSettingSchemaValidation"
              v-model="activeStep"
              class="stepper"
              elevation="0"
            >
              <v-stepper-header>
                <v-stepper-step
                  editable
                  :complete="activeStep > 1"
                  step="1"
                  color="#9BBDFF"
                >
                  <span class="step_header_label">
                    {{ $t("utils.settings") }}
                  </span>
                </v-stepper-step>
                <v-stepper-step editable step="2" color="#9BBDFF">
                  <span class="step_header_label"
                    >{{ $t("utils.validation_schemes") }}
                  </span>
                </v-stepper-step>
              </v-stepper-header>
            </v-stepper>
            <v-stepper v-model="activeStep" elevation="0">
              <v-stepper-items>
                <v-stepper-content step="1">
                  <div class="step_body">
                    <base-text-field
                      v-model="form.title"
                      :label="$t('utils.title')"
                      :has-error="!!titleErrors"
                      :error-messages="titleErrors"
                      class="mt-1"
                    />
                    <div class="fileds_container">
                      <!-- WORKFLOW FIELDS -->
                      <div class="section">
                        <div
                          class="section_title"
                          v-text="$t('utils.fiels_available')"
                        />
                        <div class="section_header">
                          <v-text-field
                            v-model="wfQuestions.name"
                            class="search_field"
                            outlined
                            :placeholder="`${$t('utils.search')}...`"
                            prepend-inner-icon="$mdiMagnify"
                            dense
                            hide-details
                            @input="filterWfQuestions()"
                          />
                        </div>
                        <div class="section_body">
                          <button
                            v-for="(question, i) in formatedQuestions"
                            :key="`q-${i}`"
                            class="question_item"
                            @click.stop="selectQuestion(question)"
                          >
                            <div class="info_section">
                              <div
                                class="question_label"
                                v-text="question.label"
                              />
                              <div
                                class="question_slug"
                                v-text="question.slug"
                              />
                            </div>
                            <div class="action">
                              <BaseButtonIcon
                                color="primary"
                                icon="$mdiArrowRight"
                                @click.stop="selectQuestion(question)"
                              />
                            </div>
                          </button>
                          <template v-if="wfQuestions.loading">
                            <div
                              v-for="value in 5"
                              :key="value"
                              class="folder_item_skeleton"
                            >
                              <v-skeleton-loader class="skeleton" type="text" />
                            </div>
                          </template>
                          <div v-else v-intersect="loadMoreWorkflowQuestions" />
                        </div>
                      </div>
                      <!-- FORM FIELDS -->
                      <div class="section">
                        <div
                          class="section_title"
                          v-text="$t('utils.form_fields')"
                        />
                        <div class="section_header">
                          <v-text-field
                            v-model="search_str"
                            class="search_field"
                            outlined
                            :placeholder="`${$t('utils.search')}...`"
                            prepend-inner-icon="$mdiMagnify"
                            dense
                            hide-details
                          />
                        </div>
                        <div
                          class="section_body"
                          :class="{
                            error_section: !!questionErrors,
                          }"
                        >
                          <div
                            v-if="questionErrors"
                            class="error_message"
                            v-text="questionErrors"
                          />
                          <button
                            v-for="(question, i) in filteredSelectedQuestions"
                            :key="`q-${i}`"
                            class="question_item"
                            @click.stop="unSelectQuestion(question)"
                          >
                            <div class="info_section">
                              <div
                                class="question_label"
                                v-text="question.label"
                              />
                              <div
                                class="question_slug"
                                v-text="question.slug"
                              />
                            </div>
                            <div class="action">
                              <BaseButtonIcon
                                color="secondary"
                                icon="$mdiClose"
                                @click.stop="unSelectQuestion(question)"
                              />
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                    <BaseMarkdownEditor
                      v-model="form.description"
                      :label="$t('organization.formDescription')"
                    />
                  </div>
                </v-stepper-content>
                <v-stepper-content step="2">
                  <!-- VALIDATION_SCHEMA SECTION -->
                  <div class="validation_schemas_section">
                    <v-skeleton-loader
                      v-if="validationSchema.loading"
                      class="validation_schema_skeleton"
                      type="text"
                    />
                    <template v-else-if="filteredSchemaValidation.length">
                      <span
                        class="sub-title mt-2"
                        v-text="$t('utils.validation_schemes')"
                      />
                      <div class="validation_schemas">
                        <div
                          v-for="(item, i) in filteredSchemaValidation"
                          :key="`vs${i}`"
                          class="schema_item"
                        >
                          <input
                            :checked="form.validationSchema.includes(item.id)"
                            type="checkbox"
                            class="item_checkbox"
                            @change="handleSchemaValidationCheck($event, item)"
                          />
                          <div>
                            <div class="check_label" v-text="item.name" />
                            <div class="quesiton_label">
                              <strong
                                v-text="
                                  `${$t('cosmoform.dialog.concernedFields')} : `
                                "
                              />
                              <span v-text="item.questionsLabel" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </div>
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            v-if="activeStep == 2"
            class="ml-auto"
            type="primary"
            outlined
            @click="activeStep = 1"
          >
            <Base-icon small icon="$mdiArrowLeftThick" />
            <span class="ml-2" v-text="$t('utils.back')" />
          </BaseButton>
          <BaseButton
            v-if="canSubmitForm"
            :disabled="isSubmiting"
            :loading="isSubmiting"
            color="primary"
            type="primary"
            @click="submit"
          >
            {{ cosmoformTemplate ? $t("utils.edit") : $t("utils.create") }}
          </BaseButton>
          <BaseButton v-else type="primary" @click="handleNextStep()">
            <span class="mr-2" v-text="$t('utils.next')" />
            <Base-icon small icon="$mdiArrowRightThick" />
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  patchOrganizationWorkflowCosmoformTemplate,
  postOrganizationWorkflowCosmoformTemplate,
  getWorkflowQuestions,
} from "../Services/workflow.service";
import { required } from "vuelidate/lib/validators";
import { maxLength } from "vuelidate/lib/validators";
import { minLength } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { CosmoformTemplate } from "@/core/Models/Cosmoform";
import organizationApi from "@/core/Services/organization.api";

export default {
  name: "CosmoformTemplateCreateDialog",
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    cosmoformTemplate: {
      type: CosmoformTemplate,
      default: () => null,
    },
  },
  data() {
    return {
      activeStep: 1,
      loading: false,
      isSubmiting: false,
      search_str: "",
      form: {
        title: "",
        description: "",
        questions: [],
        validationSchema: [],
      },
      wfQuestions: {
        page: 1,
        pageSize: 10,
        name: undefined,
        items: [],
        loading: true,
      },
      validationSchema: {
        loading: false,
        items: [],
      },
    };
  },
  validations() {
    let validations = {};
    validations.form = {
      title: {
        required,
        maxLength: maxLength(255),
      },
      questions: {
        required,
        minLength: minLength(1),
      },
    };
    return validations;
  },
  computed: {
    ...mapState({
      activeOrganization: (state) => state.organization.activeOrganization,
    }),
    modalTitle() {
      return this.cosmoformTemplate
        ? this.$t("form_template_dialog.edit_title")
        : this.$t("form_template_dialog.create_title");
    },
    titleErrors() {
      if (!this.$v.form.title.$dirty) {
        return "";
      }
      if (!this.$v.form.title.required) {
        return this.$t("cosmoform.dialog.titleRequired");
      }
      return "";
    },
    questionErrors() {
      if (!this.$v.form.questions.$dirty) {
        return "";
      }
      if (!this.$v.form.questions.required) {
        return this.$t("utils.field_is_required");
      }
      return "";
    },
    formatedQuestions() {
      return this.wfQuestions.items
        .map((q) => {
          return { ...q, label: `${q.name} ( ${q.status} )` };
        })
        .filter((q) => !this.form.questions.some((fq) => fq.id == q.id));
    },
    filteredSelectedQuestions() {
      return this.form.questions
        .map((q) => ({
          ...q,
          label: q.status ? `${q.name} ( ${q.status} )` : q.name,
        }))
        .filter((q) =>
          !this.search_str
            ? q
            : q.label.toLowerCase().includes(this.search_str.toLowerCase())
        );
    },
    filteredSchemaValidation() {
      return this.validationSchema.items.filter((vs) => {
        if (!vs.questions.length || !this.form.questions.length) return;
        return vs.questions.every((q) =>
          this.form.questions.some((sq) => sq.slug == q.slug)
        );
      });
    },
    needSettingSchemaValidation() {
      return !!this.filteredSchemaValidation.length;
    },
    canSubmitForm() {
      if (!this.needSettingSchemaValidation) return true;
      return this.activeStep == 2;
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async fetchValidationSchema() {
      this.validationSchema.loading = true;
      try {
        const res = await organizationApi.getOrganizationValidationSchema(
          this.organizationId
        );
        this.validationSchema.items = res.data.results.map((vs) => ({
          ...vs,
          questionsLabel: vs.questions.map((q) => q.name).join(", "),
          checked: false,
        }));
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.validationSchema.loading = false;
    },
    handleNextStep() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.activeStep = 2;
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.isSubmiting = true;
      try {
        const payload = {
          questions: this.form.questions.map((q) => q.id),
          title: this.form.title,
          description: this.form.description,
        };
        if (this.form.validationSchema.length)
          payload.validation_schemas = [...this.form.validationSchema].filter(
            (id) => this.filteredSchemaValidation.some((vs) => vs.id === id)
          );
        if (this.cosmoformTemplate) {
          const response = await patchOrganizationWorkflowCosmoformTemplate({
            organizationId: this.organizationId,
            workflowId: this.$route.params.workflowId,
            payload,
            templateId: this.cosmoformTemplate.id,
          });
          this.$emit("cosmoform-edited", response.data);
          this.$store.dispatch("snackbar/active", {
            message: this.$t("workflow.cosmoform.edited_successfully"),
            type: "SUCCESS",
          });
        } else {
          await postOrganizationWorkflowCosmoformTemplate({
            organizationId: this.organizationId,
            workflowId: this.$route.params.workflowId,
            payload,
          });
          this.$emit("cosmoform-created");

          this.$store.dispatch("snackbar/active", {
            message: this.$t("workflow.cosmoform.created_successfully"),
            type: "SUCCESS",
          });
        }
      } catch (error) {
        let msg = this.$t("utils.errorHasHappen");
        if (error?.response?.data?.code === "missing-questions-for-control") {
          msg = "";
          const value = error?.response?.data?.details;
          for (const key in value) {
            const values = value[key].join(", ");
            msg = `${msg} \n ${key}: ${this.$t("utils.fieldMissing", {
              field: values,
            })}`;
          }
        }
        this.$store.dispatch("snackbar/active", {
          message: msg,
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
    reset() {
      this.form = {
        title: "",
        description: "",
        questions: [],
      };
      this.$v.form.$reset();
      this.$emit("close");
    },
    async initialize() {
      await Promise.all([
        this.fetchWorkflowQuestion(),
        this.fetchValidationSchema(),
      ]);
      if (this.cosmoformTemplate) {
        this.form.title = this.cosmoformTemplate.title;
        this.form.questions = this.cosmoformTemplate.questions;
        this.form.validationSchema = [
          ...this.cosmoformTemplate.validationSchemas,
        ];
      }
      this.form.description =
        this.cosmoformTemplate?.description ||
        this.activeOrganization.cosmoformDefaultMessage ||
        this.$t("cosmoform.instruction");
    },

    async fetchWorkflowQuestion() {
      this.wfQuestions.loading = true;
      try {
        this.wfQuestions.page = 1;
        const response = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.wfQuestions.page,
          name: this.wfQuestions.name,
          pageSize: this.wfQuestions.pageSize,
          std_only: true,
        });
        this.wfQuestions = { ...this.wfQuestions, ...response };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.wfQuestions.loading = false;
    },
    filterWfQuestions() {
      if (this.wfQuestions.searchTimeoutRef)
        clearTimeout(this.wfQuestions.searchTimeoutRef);
      this.wfQuestions.searchTimeoutRef = setTimeout(async () => {
        this.fetchWorkflowQuestion();
      }, 200);
    },
    async loadMoreWorkflowQuestions(_, __, isIntersecting) {
      if (!isIntersecting || this.wfQuestions.loading || !this.wfQuestions.next)
        return;
      this.wfQuestions.page = this.wfQuestions.next;
      this.wfQuestions.loading = true;
      try {
        const tmp = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.wfQuestions.page,
          name: this.wfQuestions.name,
          pageSize: this.wfQuestions.pageSize,
          std_only: true,
        });
        this.wfQuestions = {
          ...this.wfQuestions,
          ...tmp,
          items: [...this.wfQuestions.items, ...tmp.items],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.wfQuestions.loading = false;
    },
    selectQuestion(question) {
      this.form.questions.unshift(question);
    },
    unSelectQuestion(question) {
      this.form.questions = this.form.questions.filter(
        (q) => q.id != question.id
      );
    },
    handleSchemaValidationCheck(event, validationSchema) {
      const checked = event?.target?.checked;
      this.form.validationSchema = this.form.validationSchema.filter(
        (vs) => vs != validationSchema.id
      );
      if (checked) this.form.validationSchema.push(validationSchema.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  position: relative;

  .stepper_container {
    height: 100%;
    .stepper {
      width: 100%;
    }
    &::v-deep .theme--light.v-stepper {
      background: none;
    }
    &::v-deep .v-stepper__header {
      height: 64px;
    }
    &::v-deep .v-stepper__step {
      height: 64px;
      padding: 24px 0px;
    }
    &::v-deep .v-stepper__header {
      box-shadow: none;
    }
    &::v-deep .v-stepper__content {
      padding: 17px 0 0;
    }

    &::v-deep .v-stepper__step--inactive {
      .v-stepper__step__step {
        border-radius: 23px;
        color: black;
        border: solid 5px rgba(235, 235, 235, 0.3) !important;
        background: white !important;
      }
    }
    &::v-deep .v-stepper__step__step {
      width: 32px;
      height: 32px;
      border-radius: 23px;
      border: solid 5px #eaf1ff !important;
      .v-icon__svg {
        height: 18px;
        width: 18px;
      }
    }
  }
  .sub-title {
    font-family: "Inter";
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #353542;
  }
  .input-fields {
    padding: 16px 16px 0 16px;
    margin-bottom: 24px;
    .input_label {
      color: #353542;
      font-family: "Inter";
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      margin-bottom: 16px;
    }
  }
  .offer-nature-container {
    display: flex;
    gap: 24px;
    margin-top: 24px;
    .offer-item {
      flex: 1;
    }
  }
  .file-drag {
    width: 400px;
    height: 100px;
    background: lightgrey;
  }
  ::v-deep .v-menu__content {
    top: 44px !important;
  }

  ::v-deep .v-skeleton-loader__text {
    height: 40px;
  }

  ::v-deep .v-skeleton-loader__heading .v-skeleton-loader__bone {
    display: none;
  }
  .action-container {
    display: flex;
    align-items: center;
    margin-top: 32px;
  }
  .fileds_container {
    width: 100%;
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    .section {
      flex: none;
      width: calc(50% - 10px);
      .section_title {
        font-size: 16px;
        line-height: 20px;
        margin-bottom: 10px;
        color: #353542;
      }
      .section_header {
        padding: 8px;
        background: #ecebf1;
        .search_field {
          background: #fff;
        }
      }
      .section_body {
        position: relative;
        border: 0.5px solid #ecebf1;
        padding: 12px;
        height: 312px;
        overflow: hidden;
        overflow-y: auto;
        &.error_section {
          border-color: red;
        }
        .question_item {
          padding: 6px 10px;
          align-items: center;
          display: flex;
          width: 100%;
          gap: 4px;
          border-radius: 5px;
          margin-bottom: 12px;
          cursor: pointer;
          text-align: start;
          background: #fff;
          transition: all 0.25s ease-in-out;
          &:hover {
            background: #f4f7ff;
          }
          .info_section {
            width: calc(100% - 40px);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            .question_label {
              color: #2536cc;
              font-size: 12px;
              font-weight: 600;
              line-height: 20px;
            }
            .question_slug {
              color: #707080;
              font-size: 12px;
              font-weight: 400;
              line-height: 16px;
              text-transform: uppercase;
            }
          }
          .action {
            width: 36px;
            height: 36px;
            flex: none;
          }
        }
        .error_message {
          text-align: center;
          color: red;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        .folder_item_skeleton {
          width: 100%;
          height: 44px;
          margin-bottom: 12px;
          .skeleton {
            height: 100%;
            width: 100%;
            ::v-deep {
              .v-skeleton-loader__bone {
                height: 100%;
              }
            }
          }
        }
      }
    }
  }
  .validation_schemas_section {
    margin-top: 20px;
    .validation_schema_skeleton {
      height: 64px;
      width: 100%;
      margin-bottom: 24px;
      ::v-deep {
        .v-skeleton-loader__bone {
          height: 64px;
        }
      }
    }
    .validation_schemas {
      padding: 0 24px;
      margin-bottom: 16px;
      .schema_item {
        display: flex;
        gap: 8px;
        padding: 8px 0;
        .item_checkbox {
          width: 18px;
          height: 18px;
          cursor: pointer;
          margin-top: 2px;
        }
        .check_label {
          color: #333;
          font-family: Inter;
          font-size: 14px;
          line-height: 20px;
        }
        .quesiton_label {
          color: #666;
          font-family: Inter;
          font-size: 12px;
          line-height: 18px;
          strong {
            font-weight: bold;
          }
        }
      }
    }
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  margin-top: 16px;
  padding: 8px 0;
  gap: 16px;
}
</style>
